import {
	Box,
	Button,
	VStack,
	Heading,
	Text,
	FormControl,
	FormLabel,
	Input,
	InputGroup,
	InputRightElement,
	Icon,
	HStack,
	Flex,
} from '@chakra-ui/react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useRef, useState, useEffect } from 'react';
import { AgenticStepLoader } from '../AgenticStepLoader';
import { StepFooter } from './StepFooter';

export interface CredentialsFormValues {
	partsTraderUsername: string;
	partsTraderPassword: string;
	cccUsername: string;
	cccPassword: string;
}

interface CredentialsFormProps {
	onPull: (values: CredentialsFormValues) => void;
	isLoading?: boolean;
	statusMessage?: string;
	initialValues?: Partial<CredentialsFormValues>;
	onLoadTestCredentials?: () => void;
	canLoadTestCredentials?: boolean;
	onBack?: () => void;
	usesPt?: boolean;
}

const AGENTIC_STEPS_FULL = [
	{ label: 'Submitting credentials', description: 'Securely sending your login details' },
	{ label: 'Pulling from PartsTrader', description: 'Fetching your PartsTrader locations' },
	{ label: 'Pulling from CCC ONE', description: 'Fetching your CCC ONE locations' },
	{ label: 'Matching locations', description: 'Reconciling data across platforms' },
];

const AGENTIC_STEPS_CCC_ONLY = [
	{ label: 'Submitting credentials', description: 'Securely sending your login details' },
	{ label: 'Pulling from CCC ONE', description: 'Fetching your CCC ONE locations' },
];

export const CredentialsForm = ({
	onPull,
	isLoading = false,
	statusMessage,
	initialValues = {},
	onLoadTestCredentials,
	canLoadTestCredentials = false,
	onBack,
	usesPt = true,
}: CredentialsFormProps) => {
	const [partsTraderUsername, setPartsTraderUsername] = useState(initialValues.partsTraderUsername ?? '');
	const [partsTraderPassword, setPartsTraderPassword] = useState(initialValues.partsTraderPassword ?? '');
	const [cccUsername, setCccUsername] = useState(initialValues.cccUsername ?? '');
	const [cccPassword, setCccPassword] = useState(initialValues.cccPassword ?? '');
	const [showPtPassword, setShowPtPassword] = useState(false);
	const [showCccPassword, setShowCccPassword] = useState(false);
	const [progressStep, setProgressStep] = useState(0);
	const formRef = useRef<HTMLFormElement>(null);

	const agenticSteps = usesPt ? AGENTIC_STEPS_FULL : AGENTIC_STEPS_CCC_ONLY;

	useEffect(() => {
		if (!isLoading) {
			setProgressStep(0);
			return;
		}
		const interval = setInterval(() => {
			setProgressStep((prev) => Math.min(prev + 1, agenticSteps.length - 1));
		}, 12000);
		return () => clearInterval(interval);
	}, [isLoading, agenticSteps.length]);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		onPull({
			partsTraderUsername,
			partsTraderPassword,
			cccUsername,
			cccPassword,
		});
	};

	const hasCredentials =
		cccUsername && cccPassword && (usesPt ? partsTraderUsername && partsTraderPassword : true);

	if (isLoading) {
		return (
			<Flex align="center" justify="center" width="100%" flex={1} minH="400px">
				<AgenticStepLoader
					title="Pulling location data"
					subtitle="Please do not close or refresh this page. This may take a few minutes."
					steps={agenticSteps}
					currentStep={progressStep}
				/>
			</Flex>
		);
	}

	return (
		<Flex as="form" ref={formRef} onSubmit={handleSubmit} direction="column" flex={1} width="100%" minH={0}>
			{/* Content area — scrollable if needed, vertically centered */}
			<Flex flex={1} minH={0} overflowY="auto" align="center" justify="center">
				<VStack spacing={6} maxW="480px" mx="auto" px={{ base: 2, md: 4 }} py={4}>
					{/* Header */}
					<Box textAlign="center">
						<Heading size="md" mb={1}>Connect Your Accounts</Heading>
						<Text color="gray.500" fontSize="sm" whiteSpace="nowrap">
							{usesPt
								? 'Enter your portal credentials to pull location data from PartsTrader and CCC ONE.'
								: 'Enter your CCC ONE credentials to pull location data.'}
						</Text>
						{canLoadTestCredentials && onLoadTestCredentials && (
							<Button size="xs" variant="outline" onClick={onLoadTestCredentials} mt={3}>
								Load test credentials
							</Button>
						)}
					</Box>

				{/* PartsTrader card (hidden when usesPt=false) */}
				{usesPt && (
					<Box
						width="100%"
						bg="white"
						borderRadius="xl"
						boxShadow="none"
						overflow="hidden"
					>
						<Box bg="blue.600" px={5} py={2}>
							<HStack spacing={2}>
								<Flex w="28px" h="28px" borderRadius="md" bg="whiteAlpha.200" align="center" justify="center">
									<Text fontSize="xs" fontWeight="bold" color="white">PT</Text>
								</Flex>
							</HStack>
						</Box>
						<Box px={5} py={4}>
							<VStack spacing={3} align="stretch">
								<FormControl isRequired>
									<FormLabel fontSize="sm">Username</FormLabel>
									<Input
										value={partsTraderUsername}
										onChange={(e) => setPartsTraderUsername(e.target.value)}
										placeholder="Enter your PartsTrader username"
										autoComplete="off"
									/>
								</FormControl>
								<FormControl isRequired>
									<FormLabel fontSize="sm">Password</FormLabel>
									<InputGroup>
										<Input
											type={showPtPassword ? 'text' : 'password'}
											value={partsTraderPassword}
											onChange={(e) => setPartsTraderPassword(e.target.value)}
											placeholder="Enter your password"
											autoComplete="off"
										/>
										<InputRightElement>
											<Flex onClick={() => setShowPtPassword(!showPtPassword)} cursor="pointer">
												<Icon
													as={(showPtPassword ? FaEyeSlash : FaEye) as React.ElementType}
													color="gray.400"
													mr={2}
												/>
											</Flex>
										</InputRightElement>
									</InputGroup>
								</FormControl>
							</VStack>
						</Box>
					</Box>
				)}

				{/* CCC ONE card */}
					<Box
						width="100%"
						bg="white"
						borderRadius="xl"
						boxShadow="none"
						overflow="hidden"
					>
						<Box bg="blue.300" px={5} py={2}>
							<HStack spacing={2}>
								<Flex w="28px" h="28px" borderRadius="md" bg="whiteAlpha.200" align="center" justify="center">
									<Text fontSize="xs" fontWeight="bold" color="white">C</Text>
								</Flex>
							</HStack>
						</Box>
						<Box px={5} py={4}>
							<VStack spacing={3} align="stretch">
								<FormControl isRequired>
									<FormLabel fontSize="sm">Username</FormLabel>
									<Input
										value={cccUsername}
										onChange={(e) => setCccUsername(e.target.value)}
										placeholder="Enter your CCC ONE username"
										autoComplete="off"
									/>
								</FormControl>
								<FormControl isRequired>
									<FormLabel fontSize="sm">Password</FormLabel>
									<InputGroup>
										<Input
											type={showCccPassword ? 'text' : 'password'}
											value={cccPassword}
											onChange={(e) => setCccPassword(e.target.value)}
											placeholder="Enter your password"
											autoComplete="off"
										/>
										<InputRightElement>
											<Flex onClick={() => setShowCccPassword(!showCccPassword)} cursor="pointer">
												<Icon
													as={(showCccPassword ? FaEyeSlash : FaEye) as React.ElementType}
													color="gray.400"
													mr={2}
												/>
											</Flex>
										</InputRightElement>
									</InputGroup>
								</FormControl>
							</VStack>
						</Box>
					</Box>
				</VStack>
			</Flex>

			<StepFooter
				back={onBack ? { onClick: onBack } : undefined}
				primary={{
					label: 'Pull Accounts',
					onClick: () => formRef.current?.requestSubmit(),
					disabled: !hasCredentials,
					isLoading,
					loadingText: 'Pulling locations...',
				}}
			/>
		</Flex>
	);
};
